import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  isValidElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useState,
} from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';

import { contentRecipe, dialogPageGridStyle, overlayStyle, scrollContainerStyle } from './Dialog.css';
import { CloseIcon } from '../../icons';
import { useI18nTranslation } from '../../providers/i18n';
import { getClassNames } from '../../util/css';
import { RecipeVariants } from '../../vanilla-extract.css';
import { Box } from '../Box/Box';
import { Heading } from '../Heading/Heading';
import { IconButton } from '../IconButton/IconButton';
import { Stack } from '../Stack/Stack';

type StateProps =
  | {
      isOpen?: never;
      setOpen?: never;
      /** Interactable element that will trigger the opening of the Dialog */
      trigger: ReactNode;
    }
  | {
      /** Controlled state of the modal */
      isOpen: boolean;
      /** State setter for the controlled state. Required in order to use built-in Dialog controls. */
      setOpen: Dispatch<SetStateAction<boolean>>;
      /** Interactable element that will trigger the opening of the Dialog */
      trigger?: ReactNode;
    };

export type SharedProps = {
  /** Description that will be announced to screenreaders and be visible in the Dialog */
  description?: ReactNode;
  /** Function that is triggerered when the user closes the dialog */
  onClose?(event?: SyntheticEvent): void;
  /** Title that will be shown as the header of the Dialog. */
  title?: ReactNode;
} & StateProps;

type DialogVariants = RecipeVariants<typeof contentRecipe>;

type Props = SharedProps & DialogVariants;

const getNodeText = (node?: ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return getNodeText(node.props.children);
  return '';
};

const getCloseButtonLabel = (closeText: string, closeDialogText: string, title?: ReactNode): string => {
  const titleText = getNodeText(title).trim();
  return titleText ? closeDialogText.replace('{title}', titleText) : closeText;
};

export { contentRecipe, dialogPageGridStyle, overlayStyle, scrollContainerStyle };
export { contentWithPaddingStyle } from './Dialog.css';

export const Dialog = (({
  children,
  description,
  fullscreen,
  isOpen,
  onClose,
  setOpen,
  title,
  trigger,
  width = 'regular',
}) => {
  const closeText = useI18nTranslation('close');
  const closeDialogText = useI18nTranslation('closeDialog');
  const closeLabel = getCloseButtonLabel(closeText, closeDialogText, title);
  const [hasHandledClose, setHasHandledClose] = useState(false);

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen?.(isOpen);
      if (isOpen) setHasHandledClose(false);
      if (!isOpen && !hasHandledClose) onClose?.();
    },
    [hasHandledClose, onClose, setOpen],
  );

  const onCloseHandler = useCallback(
    (event: SyntheticEvent<HTMLButtonElement | HTMLAnchorElement, Event>) => {
      onClose?.(event);
      setHasHandledClose(true);
    },
    [onClose, setHasHandledClose],
  );

  const contentClassNames = getClassNames(contentRecipe({ fullscreen, width }));

  return (
    <DialogPrimitive.Root onOpenChange={onOpenChange} open={isOpen}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={overlayStyle}>
          <div className={dialogPageGridStyle}>
            <DialogPrimitive.Content className={contentClassNames}>
              <Stack alignX="end">
                <Box paddingRight="2" paddingTop="2">
                  <DialogPrimitive.Close asChild>
                    <IconButton label={closeLabel} onClick={onCloseHandler}>
                      <CloseIcon size="small" />
                    </IconButton>
                  </DialogPrimitive.Close>
                </Box>
              </Stack>
              <Box className={scrollContainerStyle} paddingX="6" paddingBottom="6" overflow="auto">
                {title && (
                  <Box paddingBottom={{ initial: '2', md: '4' }}>
                    <DialogPrimitive.Title asChild>
                      {typeof title === 'string' ? (
                        <Heading as="h2" size="S">
                          {title}
                        </Heading>
                      ) : (
                        title
                      )}
                    </DialogPrimitive.Title>
                  </Box>
                )}
                {description && (
                  <DialogPrimitive.Description asChild>
                    <Box paddingBottom={{ initial: '4', md: '5' }}>{description}</Box>
                  </DialogPrimitive.Description>
                )}
                {children}
              </Box>
            </DialogPrimitive.Content>
          </div>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}) as DialogProps;

interface DialogProps extends FC<PropsWithChildren<Props>> {
  Close: typeof DialogPrimitive.Close;
}

Dialog.Close = DialogPrimitive.Close;
Dialog.Close.displayName = 'Dialog.Close';
